import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Button, Icon, Input } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { TagChip } from './TagChip.js';
import { type DecryptedTag } from './tagCache.js';

interface Props {
  open: boolean;
  // The photo whose tags we edit. Single-photo for now — bulk attach is a
  // future iteration in the bottom action sheet.
  photoId: string;
  albumId: string;
  rootAlbumId: string;
  // Pre-decrypted tag list of the root, sourced from TagsListPage / parent.
  // We mutate this list when the user creates a new tag inline.
  availableTags: DecryptedTag[];
  // Album key (K_album of the root) for encrypting new tag names.
  albumKey: CryptoKey;
  onClose: () => void;
  // Notify parent that the available-tags list grew (a new tag was created).
  onTagCreated?: (tag: DecryptedTag) => void;
  // Notify parent that this photo's attached set changed (so it can refresh
  // any cached tag chips it renders for the photo).
  onAttachedChanged?: (attachedTagIds: Set<string>) => void;
}

// Modal that lets the owner toggle tag membership for a single photo and
// create new tags inline. Loads the current attached set on open from
// /photos/{id}/tags so we don't drift from the server.
export function TagPicker({
  open,
  photoId,
  albumId,
  rootAlbumId,
  availableTags,
  albumKey,
  onClose,
  onTagCreated,
  onAttachedChanged,
}: Props) {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [attached, setAttached] = useState<Set<string> | 'loading' | { error: string }>('loading');
  const [pendingTagIds, setPendingTagIds] = useState<Set<string>>(new Set());
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  // Load attached tag ids when the modal opens. Re-runs on photo change in
  // the rare case the parent reopens the picker for a different tile.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setAttached('loading');
    setPendingTagIds(new Set());
    setNewTagName('');
    setCreateError(null);
    setFilter('');
    (async () => {
      try {
        const resp = await metadata.listPhotoTags(photoId, rootAlbumId, session.jwt);
        if (cancelled) return;
        setAttached(new Set(resp.tags.map((t) => t.tagId)));
      } catch (err) {
        if (!cancelled) setAttached({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, photoId, rootAlbumId, session.jwt, metadata]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const sortedTags = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return [...availableTags].sort((a, b) => collator.compare(a.name, b.name));
  }, [availableTags]);

  const filteredTags = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sortedTags;
    return sortedTags.filter((t) => t.name.toLowerCase().includes(q));
  }, [sortedTags, filter]);

  if (!open) return null;

  const attachedSet = attached instanceof Set ? attached : null;

  // Attach/detach for a single tag with optimistic state. Failures roll back
  // and surface a non-blocking error string in the dialog footer.
  async function toggleTag(tag: dto.TagDefinition) {
    if (!attachedSet) return;
    const isAttached = attachedSet.has(tag.id);
    setPendingTagIds((s) => new Set(s).add(tag.id));
    const next = new Set(attachedSet);
    if (isAttached) next.delete(tag.id);
    else next.add(tag.id);
    setAttached(next);
    try {
      if (isAttached) {
        await metadata.detachTagsBatch(
          { rootAlbumId, items: [{ tagId: tag.id, photoId }] },
          session.jwt,
        );
      } else {
        await metadata.attachTagsBatch(
          { rootAlbumId, items: [{ tagId: tag.id, photoId, albumId }] },
          session.jwt,
        );
      }
      onAttachedChanged?.(next);
    } catch (err) {
      // Roll back on failure.
      setAttached(attachedSet);
      window.alert(err instanceof Error ? err.message : 'errore');
    } finally {
      setPendingTagIds((s) => {
        const c = new Set(s);
        c.delete(tag.id);
        return c;
      });
    }
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    const name = newTagName.trim();
    if (!name) {
      setCreateError('inserisci un nome');
      return;
    }
    // Reject duplicate names (case-insensitive) — TagDefinition allows it
    // (id is a UUID) but it would confuse the user.
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    if (availableTags.some((t) => collator.compare(t.name, name) === 0)) {
      setCreateError('esiste già un tag con questo nome');
      return;
    }
    setCreating(true);
    try {
      const tagId = crypto.randomUUID();
      const encryptedName = await cv.encryptText(name, albumKey);
      const created = await metadata.createTag({ rootAlbumId, tagId, encryptedName }, session.jwt);
      const decrypted: DecryptedTag = { tag: created, name };
      onTagCreated?.(decrypted);
      setNewTagName('');
      // Auto-attach the newly created tag to the photo, since that's why the
      // user is in this dialog.
      await toggleTag(created);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'errore');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Modifica tag"
      className="fixed inset-0 z-[110] flex items-end justify-center bg-navy/60 backdrop-blur-sm sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-3xl border border-outline bg-surface-container p-6 shadow-elevated sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan/10 text-cyan">
              <Icon name="sell" className="text-xl" filled />
            </span>
            <div>
              <h2 className="font-display text-lg font-semibold">Modifica tag</h2>
              <p className="text-xs text-on-surface-variant">
                Tocca un tag per applicarlo o rimuoverlo.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="rounded-full p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-on-surface"
          >
            <Icon name="close" />
          </button>
        </div>

        <form onSubmit={handleCreate} className="mt-5 flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="Nuovo tag"
              placeholder="es. Mare"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              {...(createError ? { error: createError } : {})}
              disabled={creating}
            />
          </div>
          <Button
            type="submit"
            loading={creating}
            leftIcon={<Icon name="add" />}
            disabled={creating || newTagName.trim().length === 0}
          >
            Crea
          </Button>
        </form>

        {availableTags.length > 4 ? (
          <div className="mt-4">
            <Input
              placeholder="Filtra…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              leftIcon={<Icon name="search" className="text-base" />}
            />
          </div>
        ) : null}

        <div className="mt-4 min-h-[3rem] flex-1 overflow-y-auto">
          {attached === 'loading' ? (
            <p className="py-2 text-sm text-on-surface-variant">Carico tag…</p>
          ) : 'error' in attached ? (
            <p className="py-2 text-sm text-error">Errore: {attached.error}</p>
          ) : availableTags.length === 0 ? (
            <p className="py-2 text-sm text-on-surface-variant">
              Nessun tag esistente. Creane uno qui sopra.
            </p>
          ) : filteredTags.length === 0 ? (
            <p className="py-2 text-sm text-on-surface-variant">Nessun tag corrisponde.</p>
          ) : (
            <div className="flex flex-wrap gap-2 py-1">
              {filteredTags.map((dt) => (
                <TagChip
                  key={dt.tag.id}
                  name={dt.name}
                  active={attached.has(dt.tag.id)}
                  pending={pendingTagIds.has(dt.tag.id)}
                  onClick={() => void toggleTag(dt.tag)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-end">
          <Button variant="ghost" onClick={onClose}>
            Fatto
          </Button>
        </div>
      </div>
    </div>
  );
}
